/**
 * MaintainX-Style Dashboard Overview
 * Fully connected to backend endpoints
 */

import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  Wrench,
  Clock,
  CheckCircle,
  AlertTriangle,
  Package,
  ShoppingCart,
  Users,
  Building2,
  ChevronRight,
  Calendar,
  TrendingUp,
  User,
  MapPin,
  Plus,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import PendingApprovalsCard from "@/components/admin/PendingApprovalsCard";
import Net30PipelineWidget from "@/components/admin/Net30PipelineWidget";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status Badge - MaintainX style
const StatusChip = ({ status }) => {
  const styles = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    pending_review: "bg-amber-100 text-amber-800 border-amber-200",
    in_progress: "bg-blue-100 text-blue-800 border-blue-200",
    completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
    cancelled: "bg-slate-100 text-slate-600 border-slate-200",
    overdue: "bg-red-100 text-red-800 border-red-200",
    open: "bg-blue-100 text-blue-800 border-blue-200",
    closed: "bg-emerald-100 text-emerald-800 border-emerald-200",
    assigned: "bg-violet-100 text-violet-800 border-violet-200",
  };

  const labels = {
    pending: "Pending",
    pending_review: "Pending Review",
    in_progress: "In Progress",
    completed: "Completed",
    cancelled: "Cancelled",
    overdue: "Overdue",
    open: "Open",
    closed: "Closed",
    assigned: "Assigned",
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full border ${styles[status] || styles.pending}`}>
      {labels[status] || status || "Pending"}
    </span>
  );
};

// Stats Card - Clean, minimal design
const StatCard = ({ title, value, icon: Icon, link, color = "blue", loading }) => {
  const colorStyles = {
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
    emerald: "bg-emerald-50 text-emerald-600",
    violet: "bg-violet-50 text-violet-600",
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
        <div className="flex items-start justify-between">
          <div>
            <div className="h-4 bg-slate-200 rounded w-24 mb-2" />
            <div className="h-8 bg-slate-200 rounded w-16" />
          </div>
          <div className="w-12 h-12 bg-slate-100 rounded-xl" />
        </div>
      </div>
    );
  }

  const CardContent = (
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <p className="text-3xl font-bold text-slate-900 mt-1">{value ?? 0}</p>
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorStyles[color]} group-hover:scale-105 transition-transform`}>
        <Icon size={24} strokeWidth={1.5} />
      </div>
    </div>
  );

  if (link) {
    return (
      <Link 
        to={link}
        className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-slate-300 transition-all duration-200 group block"
      >
        {CardContent}
      </Link>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      {CardContent}
    </div>
  );
};

// Work Order List Item (compact)
const WorkOrderListItem = ({ workOrder }) => {
  return (
    <Link 
      to={`/admin/maintenance`}
      className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all"
    >
      {/* Icon based on type */}
      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
        <Wrench size={20} className="text-slate-600" />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-medium text-slate-900 truncate">
            {workOrder.title || workOrder.description?.slice(0, 50) || `Work Order`}
          </h4>
          <StatusChip status={workOrder.status} />
        </div>
        <p className="text-sm text-slate-500 mt-1 truncate">
          {workOrder.hospital_name || workOrder.requester_name || "General"} • {workOrder.location || "Main Building"}
        </p>
        <p className="text-xs text-slate-400 mt-1">
          {workOrder.created_at ? new Date(workOrder.created_at).toLocaleDateString() : "Recently"}
        </p>
      </div>
      
      {/* Arrow */}
      <ChevronRight size={20} className="text-slate-400 flex-shrink-0" />
    </Link>
  );
};

// Order List Item
const OrderListItem = ({ order }) => {
  const isMarketplaceOrder = !!order.order_number;
  const linkTo = isMarketplaceOrder ? "/admin/marketplace" : "/admin/orders";
  const title = isMarketplaceOrder 
    ? `${order.order_number} — ${order.items?.[0]?.description || "Order"}`
    : (order.title || order.product_name || order.description?.slice(0, 50) || "Order");
  const subtitle = order.hospital_name || order.facility_name || order.requester_name || "General";
  const amount = order.total ? `$${order.total.toLocaleString()}` : null;
  
  return (
    <Link 
      to={linkTo}
      className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all"
      data-testid={`order-item-${order.order_number || order.id || ""}`}
    >
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isMarketplaceOrder ? "bg-emerald-50" : "bg-violet-50"}`}>
        <ShoppingCart size={20} className={isMarketplaceOrder ? "text-emerald-600" : "text-violet-600"} />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-medium text-slate-900 truncate">{title}</h4>
          <StatusChip status={order.status} />
        </div>
        <div className="flex items-center gap-2 mt-1">
          <p className="text-sm text-slate-500 truncate">{subtitle}</p>
          {amount && <span className="text-sm font-semibold text-emerald-600">{amount}</span>}
        </div>
        <p className="text-xs text-slate-400 mt-1">
          {order.created_at ? new Date(order.created_at).toLocaleDateString() : "Recently"}
          {isMarketplaceOrder && order.payment_terms && ` • ${order.payment_terms === "net30" ? "Net 30" : "Pay Upon Receipt"}`}
        </p>
      </div>
      
      <ChevronRight size={20} className="text-slate-400 flex-shrink-0" />
    </Link>
  );
};

// Main Dashboard Overview Component
export default function DashboardOverview() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentMaintenance, setRecentMaintenance] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      fetchDashboardData();
    }
  }, [token]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Parse maintenance stats
        const maintenanceStats = data.maintenance_stats || {};
        const totalMaintenance = Object.values(maintenanceStats).reduce((a, b) => a + b, 0);
        const pendingMaintenance = maintenanceStats.pending || 0;
        const inProgressMaintenance = maintenanceStats.in_progress || maintenanceStats.assigned || 0;
        const completedMaintenance = maintenanceStats.completed || maintenanceStats.closed || 0;
        
        // Parse order stats
        const orderStats = data.order_stats || {};
        const totalOrders = Object.values(orderStats).reduce((a, b) => a + b, 0);
        
        // Parse user stats
        const userStats = data.user_stats || {};
        const totalContractors = userStats.contractor || 0;
        const totalHospitals = userStats.hospital || userStats.hospital_admin || 0;
        
        setStats({
          total_maintenance: totalMaintenance,
          pending_maintenance: pendingMaintenance,
          in_progress_maintenance: inProgressMaintenance,
          completed_maintenance: completedMaintenance,
          total_orders: totalOrders,
          total_contractors: totalContractors,
          total_hospitals: totalHospitals,
        });
        
        setRecentMaintenance(data.recent_maintenance || []);
        setRecentOrders(data.recent_orders || []);
      } else {
        setError("Failed to load dashboard data");
        toast.error("Failed to load dashboard data");
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      setError(error.message);
      toast.error("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const handleNewWorkOrder = () => {
    // Navigate to maintenance page where work orders can be created
    navigate("/admin/maintenance");
    toast.info("Create a new work order from the maintenance page");
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const firstName = user?.name?.split(" ")[0] || user?.full_name?.split(" ")[0] || "Admin";

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">
            {getGreeting()}, {firstName}
          </h2>
          <p className="text-slate-500 mt-1">Here's what's happening with your facilities today.</p>
        </div>
        <Button 
          onClick={handleNewWorkOrder}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus size={18} className="mr-2" />
          New Work Order
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-red-600" size={20} />
            <span className="text-red-800">Failed to load dashboard data</span>
          </div>
          <Button variant="outline" size="sm" onClick={fetchDashboardData}>
            Retry
          </Button>
        </div>
      )}

      {/* Pending hospital approvals — auto-hides when nothing is pending */}
      <PendingApprovalsCard token={token} />

      {/* Net 30 factoring pipeline — pending Flash Funding approvals */}
      <Net30PipelineWidget token={token} />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Work Orders"
          value={stats?.total_maintenance}
          icon={Wrench}
          link="/admin/maintenance"
          color="blue"
          loading={loading}
        />
        <StatCard
          title="Pending"
          value={stats?.pending_maintenance}
          icon={Clock}
          link="/admin/maintenance?status=pending"
          color="amber"
          loading={loading}
        />
        <StatCard
          title="In Progress"
          value={stats?.in_progress_maintenance}
          icon={AlertTriangle}
          link="/admin/maintenance?status=in_progress"
          color="violet"
          loading={loading}
        />
        <StatCard
          title="Completed"
          value={stats?.completed_maintenance}
          icon={CheckCircle}
          link="/admin/maintenance?status=completed"
          color="emerald"
          loading={loading}
        />
      </div>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Work Orders */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Recent Work Orders</h3>
            <Link 
              to="/admin/maintenance"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              View All <ChevronRight size={16} />
            </Link>
          </div>
          
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-lg border border-slate-200 p-4 animate-pulse">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-200 rounded-lg" />
                    <div className="flex-1">
                      <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentMaintenance.length > 0 ? (
            <div className="space-y-3">
              {recentMaintenance.slice(0, 5).map((wo, index) => (
                <WorkOrderListItem key={wo.id || index} workOrder={wo} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Wrench size={24} className="text-slate-400" />
              </div>
              <h4 className="text-base font-semibold text-slate-900 mb-1">No work orders yet</h4>
              <p className="text-sm text-slate-500 mb-4">Work orders will appear here</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleNewWorkOrder}
              >
                Create Work Order
              </Button>
            </div>
          )}
        </div>

        {/* Recent Orders */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Recent Orders</h3>
            <Link 
              to="/admin/marketplace"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              View All <ChevronRight size={16} />
            </Link>
          </div>
          
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-lg border border-slate-200 p-4 animate-pulse">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-200 rounded-lg" />
                    <div className="flex-1">
                      <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentOrders.length > 0 ? (
            <div className="space-y-3">
              {recentOrders.slice(0, 5).map((order, index) => (
                <OrderListItem key={order.id || index} order={order} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <div className="w-14 h-14 bg-violet-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingCart size={24} className="text-violet-400" />
              </div>
              <h4 className="text-base font-semibold text-slate-900 mb-1">No orders yet</h4>
              <p className="text-sm text-slate-500 mb-4">Orders will appear here</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate("/admin/orders")}
              >
                View Orders
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link 
          to="/admin/orders"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-blue-300 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center text-violet-600 group-hover:scale-105 transition-transform">
              <ShoppingCart size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Active Orders</p>
              <p className="text-2xl font-bold text-slate-900">{loading ? "..." : stats?.total_orders || 0}</p>
            </div>
          </div>
        </Link>
        
        <Link 
          to="/admin/contractors"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-blue-300 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600 group-hover:scale-105 transition-transform">
              <Users size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Contractors</p>
              <p className="text-2xl font-bold text-slate-900">{loading ? "..." : stats?.total_contractors || 0}</p>
            </div>
          </div>
        </Link>
        
        <Link 
          to="/admin/hospitals"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-blue-300 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center text-amber-600 group-hover:scale-105 transition-transform">
              <Building2 size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Hospitals</p>
              <p className="text-2xl font-bold text-slate-900">{loading ? "..." : stats?.total_hospitals || 0}</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}

// Export components for reuse
export { StatusChip, StatCard, WorkOrderListItem, OrderListItem };
